import React from 'react';
import { pdf } from '@react-pdf/renderer';
import { Label } from '@/components/ui/label';
import { FileText, Upload, CheckCircle2, Eye } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import ContractPDF, { ContractData } from '@/pdf/templates/ContractPDF';
import BillPDF, { BillData } from '@/pdf/templates/BillPDF';
import { generateQRCodeDataURL, generateCode128BarcodeDataURL } from '@/pdf/utils/barcodes';
import { toast } from 'sonner';

interface Step2TemplatesProps {
  templatesData: {
    contractFile: File | null;
    billFile: File | null;
    useDefaults: { contract: boolean; bill: boolean };
  };
  onChange: (field: string, value: File | null | { contract: boolean; bill: boolean }) => void;
}

export const Step2Templates: React.FC<Step2TemplatesProps> = ({ templatesData, onChange }) => {
  const handleFileUpload = (type: 'contract' | 'bill', e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('PDF file must be less than 10MB');
        return;
      }
      if (file.type !== 'application/pdf') {
        alert('Please upload a PDF file');
        return;
      }
      onChange(`${type}File`, file);
    }
  };

  const handlePreviewContract = async () => {
    try {
      toast.info('Generating contract preview...');
      
      const sampleContractData: ContractData = {
        title: 'ENERGY SERVICE CONTRACT',
        contractName: 'Standard Prepaid Contract',
        pdl: '24567891234567',
        pricePerKwh: 'TZS 650.00',
        subscribedPower: '9 kVA',
        contractNumber: 'GE-CON-2024-SAMPLE',
        customerName: 'Sample Customer',
        date: new Date().toLocaleDateString('en-GB'),
      };

      const verifyUrl = `${window.location.origin}/contracts/verify?id=${sampleContractData.contractNumber}`;
      const qrDataUrl = await generateQRCodeDataURL(verifyUrl, 144);
      const barcodeDataUrl = generateCode128BarcodeDataURL(sampleContractData.pdl, { height: 60, width: 2 });

      const blob = await pdf(
        <ContractPDF data={sampleContractData} qrDataUrl={qrDataUrl} barcodeDataUrl={barcodeDataUrl} />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('Contract preview opened');
    } catch (error) {
      console.error('Error generating contract preview:', error);
      toast.error('Failed to generate contract preview');
    }
  };

  const handlePreviewBill = async () => {
    try {
      toast.info('Generating bill preview...');
      
      const sampleBillData: BillData = {
        month: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        billNumber: 'GE-BILL-2024-SAMPLE',
        issueDate: new Date().toLocaleDateString('en-GB'),
        period: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        energyConsumed: '385 kWh',
        ratePerKwh: 'TZS 650.00',
        totalAmount: 'TZS 250,250.00',
        status: 'pending',
      };

      const verifyUrl = `${window.location.origin}/bills/verify?bill=${sampleBillData.billNumber}`;
      const qrDataUrl = await generateQRCodeDataURL(verifyUrl, 144);
      const barcodeDataUrl = generateCode128BarcodeDataURL(sampleBillData.billNumber, { height: 60, width: 2 });

      const blob = await pdf(
        <BillPDF data={sampleBillData} qrDataUrl={qrDataUrl} barcodeDataUrl={barcodeDataUrl} />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('Bill preview opened');
    } catch (error) {
      console.error('Error generating bill preview:', error);
      toast.error('Failed to generate bill preview');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <FileText className="h-8 w-8 text-primary" />
        <div>
          <h2 className="text-2xl font-bold text-foreground">PDF Template Upload</h2>
          <p className="text-muted-foreground">Upload custom templates or use default Growth Energy templates</p>
        </div>
      </div>

      {/* Contract Template */}
      <div className="border rounded-lg p-6 space-y-4 bg-card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg">Contract Template</h3>
            <p className="text-sm text-muted-foreground">PDF template for client contracts</p>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="useDefaultContract" className="text-sm">Use Default</Label>
            <Switch
              id="useDefaultContract"
              checked={templatesData.useDefaults.contract}
              onCheckedChange={(checked) => {
                onChange('useDefaults', { ...templatesData.useDefaults, contract: checked });
                if (checked) onChange('contractFile', null);
              }}
            />
          </div>
        </div>

        {!templatesData.useDefaults.contract && (
          <div>
            {templatesData.contractFile ? (
              <div className="flex items-center gap-3 p-4 border rounded-lg bg-muted/50">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <div className="flex-1">
                  <p className="font-medium">{templatesData.contractFile.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(templatesData.contractFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onChange('contractFile', null)}
                  className="text-destructive hover:underline text-sm"
                >
                  Remove
                </button>
              </div>
            ) : (
              <label
                htmlFor="contractUpload"
                className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-8 cursor-pointer hover:bg-muted/50 transition-colors"
              >
                <Upload className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-sm font-medium">Click to upload contract template</p>
                <p className="text-xs text-muted-foreground mt-1">PDF up to 10MB</p>
              </label>
            )}
            <input
              id="contractUpload"
              type="file"
              accept="application/pdf"
              onChange={(e) => handleFileUpload('contract', e)}
              className="hidden"
            />
          </div>
        )}

        {templatesData.useDefaults.contract && (
          <div className="flex items-center gap-2 p-4 bg-primary/10 rounded-lg">
            <CheckCircle2 className="h-5 w-5 text-primary" />
            <p className="text-sm text-muted-foreground flex-1">Using default Growth Energy contract template</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handlePreviewContract}
            >
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
          </div>
        )}
      </div>

      {/* Bill Template */}
      <div className="border rounded-lg p-6 space-y-4 bg-card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg">Bill Template</h3>
            <p className="text-sm text-muted-foreground">PDF template for monthly bills</p>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="useDefaultBill" className="text-sm">Use Default</Label>
            <Switch
              id="useDefaultBill"
              checked={templatesData.useDefaults.bill}
              onCheckedChange={(checked) => {
                onChange('useDefaults', { ...templatesData.useDefaults, bill: checked });
                if (checked) onChange('billFile', null);
              }}
            />
          </div>
        </div>

        {!templatesData.useDefaults.bill && (
          <div>
            {templatesData.billFile ? (
              <div className="flex items-center gap-3 p-4 border rounded-lg bg-muted/50">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <div className="flex-1">
                  <p className="font-medium">{templatesData.billFile.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(templatesData.billFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onChange('billFile', null)}
                  className="text-destructive hover:underline text-sm"
                >
                  Remove
                </button>
              </div>
            ) : (
              <label
                htmlFor="billUpload"
                className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-8 cursor-pointer hover:bg-muted/50 transition-colors"
              >
                <Upload className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-sm font-medium">Click to upload bill template</p>
                <p className="text-xs text-muted-foreground mt-1">PDF up to 10MB</p>
              </label>
            )}
            <input
              id="billUpload"
              type="file"
              accept="application/pdf"
              onChange={(e) => handleFileUpload('bill', e)}
              className="hidden"
            />
          </div>
        )}

        {templatesData.useDefaults.bill && (
          <div className="flex items-center gap-2 p-4 bg-primary/10 rounded-lg">
            <CheckCircle2 className="h-5 w-5 text-primary" />
            <p className="text-sm text-muted-foreground flex-1">Using default Growth Energy bill template</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handlePreviewBill}
            >
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
